export default class SubEvaluation {
    constructor({
        id = null,
        evaluation_id = null,
        submission_id = null,
        subevaluation_started = '',
        subevaluation_ended = '',
        subevaluation_successful = null,
        teacher_log_path = null,
        student_log_path = null
    }) {
        this.id = id;
        this.evaluation_id = evaluation_id;
        this.submission_id = submission_id;
        this.subevaluation_started = subevaluation_started;
        this.subevaluation_ended = subevaluation_ended;
        this.subevaluation_successful = subevaluation_successful;
        this.teacher_log_path = teacher_log_path;
        this.student_log_path = student_log_path;
    }

    // Validate required subevaluation data
    static validate(data) {
        if (!data.subevaluation_started) {
            throw new Error('Subevaluation start time is required');
        }
        if (!data.subevaluation_ended) {
            throw new Error('Subevaluation end time is required');
        }
        if (data.subevaluation_successful === null || data.subevaluation_successful === undefined) {
            throw new Error('Subevaluation success value is required');
        }
    }

    // Format date and time to DD.MM.YYYY HH:MM
    static formatDateTime(date) {
        if (!date) return '';
        if (typeof date !== 'string') {
            if (date instanceof Date && !isNaN(date)) {
                date = date.toISOString();
            } else {
                try {
                    date = new Date(date).toISOString();
                } catch (e) {
                    console.error('Invalid date:', date);
                    return '';
                }
            }
        }
        const [datePart, timePart] = date.split('T');
        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':').map(part => part.split('.')[0]);
        return `${day}.${month}.${year} ${hour}:${minute}`;
    }
    
}

export class SpecificStudentsWithEvaluation {
    constructor({
        student_id = null,
        student_uco = null,
        student_name = null,
        evaluation_id = null,
        ai_review_confidence = null,
        ai_review_approved = null
    }) {
        this.student_id = student_id;
        this.student_uco = student_uco;
        this.student_name = student_name;
        this.evaluation_id = evaluation_id;
        // AI-review summary for this student's submission (#66); null when absent.
        this.ai_review_confidence = ai_review_confidence;
        this.ai_review_approved = ai_review_approved;
    }

    // Confidence as a 0..100 integer, or null when unknown (mirrors models/aiReview.js).
    get confidencePercent() {
        if (this.ai_review_confidence === null || this.ai_review_confidence === undefined) return null;
        return Math.round(this.ai_review_confidence * 100);
    }

    // Bootstrap-5 badge class for the confidence level (colour by threshold).
    get confidenceClass() {
        const p = this.confidencePercent;
        if (p === null) return 'bg-secondary';
        if (p >= 80) return 'bg-success';
        if (p >= 50) return 'bg-warning text-dark';
        return 'bg-danger';
    }

    // Coarse bucket label shown alongside the percentage (High/Medium/Low), or null.
    get confidenceBucket() {
        const p = this.confidencePercent;
        if (p === null) return null;
        if (p >= 80) return 'High';
        if (p >= 50) return 'Medium';
        return 'Low';
    }

    static validate(specificStudentsWithEvaluationData) {
        if (!specificStudentsWithEvaluationData.student_id) {
            throw new Error('Student ID is required');
        }
        if (!specificStudentsWithEvaluationData.student_uco) {
            throw new Error('Student UCO is required');
        }
        if (!specificStudentsWithEvaluationData.evaluation_id) {
            throw new Error('Evaluation ID is required');
        }
    }
}

export class SpecifiedDeadlineSubtaskResultType {
    constructor({
        subevaluation_result_type = '',
        deadline_id = null,
        subtask_id = null
    }) {
        this.subevaluation_result_type = subevaluation_result_type;
        this.deadline_id = deadline_id;
        this.subtask_id = subtask_id;
    }

    static validate(specifiedDeadlineSubtaskResultTypeData) {
        if (!specifiedDeadlineSubtaskResultTypeData.subevaluation_result_type) {
            throw new Error('Subevaluation result type is required');
        }
        if (!this.subevaluationResultType.includes(specifiedDeadlineSubtaskResultTypeData.subevaluation_result_type)) {
            throw new Error('Invalid subevaluation result type');
        }
        if (!specifiedDeadlineSubtaskResultTypeData.deadline_id) {
            throw new Error('Deadline ID is required');
        }
        if (!specifiedDeadlineSubtaskResultTypeData.subtask_id) {
            throw new Error('Subtask ID is required');
        }
    }

    static get subevaluationResultType() {
        return ['success', 'fail', 'missing', 'container_error'];
    }
}
